import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const pathKey = (segments) => (segments.length === 0 ? '$' : `$.${segments.join('.')}`);

const isExpandable = (value) => value !== null && typeof value === 'object';

const formatPrimitive = (value) => {
  if (value === null) return 'null';
  if (typeof value === 'string') {
    const text = value.length > 280 ? `${value.slice(0, 280)}…` : value;
    return `"${text}"`;
  }
  return String(value);
};

const highlightText = (text, query) => {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return text;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="json-tree-viewer__mark">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export const collectExpandablePaths = (value, segments = [], paths = []) => {
  if (!isExpandable(value)) return paths;
  paths.push(pathKey(segments));
  if (Array.isArray(value)) {
    value.forEach((item, index) => collectExpandablePaths(item, [...segments, String(index)], paths));
  } else {
    Object.keys(value).forEach((key) => collectExpandablePaths(value[key], [...segments, key], paths));
  }
  return paths;
};

export const collectSearchPaths = (value, query, segments = [], matches = new Set()) => {
  const q = query.trim().toLowerCase();
  if (!q) return matches;

  const addPathAndAncestors = (segs) => {
    for (let i = 0; i <= segs.length; i += 1) {
      matches.add(pathKey(segs.slice(0, i)));
    }
  };

  const visit = (node, segs) => {
    const key = segs[segs.length - 1];
    if (key != null && String(key).toLowerCase().includes(q)) {
      addPathAndAncestors(segs);
    }

    if (!isExpandable(node)) {
      if (String(node).toLowerCase().includes(q)) {
        addPathAndAncestors(segs);
      }
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((item, index) => visit(item, [...segs, String(index)]));
      return;
    }

    Object.entries(node).forEach(([k, v]) => visit(v, [...segs, k]));
  };

  visit(value, segments);
  return matches;
};

const JsonTreeNode = ({
  name,
  value,
  pathSegments,
  depth,
  expandedPaths,
  searchQuery,
  searchPaths,
  onTogglePath,
}) => {
  const currentPath = pathKey(pathSegments);
  const expandable = isExpandable(value);
  const hasSearch = searchQuery.trim() !== '';
  const searchExpanded = hasSearch && searchPaths.has(currentPath);
  const expanded = searchExpanded || expandedPaths.has(currentPath);

  const childEntries = useMemo(() => {
    if (!expandable) return [];
    if (Array.isArray(value)) {
      return value.map((item, index) => [String(index), item]);
    }
    return Object.entries(value);
  }, [expandable, value]);

  const visibleInSearch = !hasSearch
    || searchPaths.has(currentPath)
    || childEntries.some(([key]) => searchPaths.has(pathKey([...pathSegments, key])));

  if (hasSearch && !visibleInSearch) {
    return null;
  }

  const typeLabel = Array.isArray(value)
    ? `Array(${value.length})`
    : expandable
      ? `Object(${Object.keys(value).length})`
      : null;

  return (
    <div className="json-tree-viewer__node" style={{ marginLeft: depth > 0 ? 16 : 0 }}>
      <div
        className={`json-tree-viewer__row${expandable ? ' json-tree-viewer__row--clickable' : ''}`}
        onClick={expandable ? () => onTogglePath(currentPath) : undefined}
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={expandable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTogglePath(currentPath);
          }
        } : undefined}
      >
        {expandable ? (
          <span className="json-tree-viewer__toggle" aria-hidden>
            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
          </span>
        ) : (
          <span className="json-tree-viewer__toggle json-tree-viewer__toggle--spacer" />
        )}
        {name != null && (
          <span className="json-tree-viewer__key">
            {highlightText(String(name), searchQuery)}
            <span className="text-muted">: </span>
          </span>
        )}
        {expandable ? (
          <span className="json-tree-viewer__type text-muted">{typeLabel}</span>
        ) : (
          <span className="json-tree-viewer__value">{highlightText(formatPrimitive(value), searchQuery)}</span>
        )}
      </div>
      {expandable && expanded && (
        <div className="json-tree-viewer__children">
          {childEntries.map(([childKey, childValue]) => (
            <JsonTreeNode
              key={`${currentPath}.${childKey}`}
              name={childKey}
              value={childValue}
              pathSegments={[...pathSegments, childKey]}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              searchQuery={searchQuery}
              searchPaths={searchPaths}
              onTogglePath={onTogglePath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const JsonTreeViewer = ({ data, expandedPaths, searchQuery, onTogglePath }) => {
  const searchPaths = useMemo(
    () => collectSearchPaths(data, searchQuery),
    [data, searchQuery]
  );

  const rootExpandable = isExpandable(data);

  if (!rootExpandable) {
    return (
      <pre className="task-detail__json mb-0 small p-2 rounded text-dark">
        {highlightText(formatPrimitive(data), searchQuery)}
      </pre>
    );
  }

  return (
    <div className="json-tree-viewer">
      <JsonTreeNode
        name={null}
        value={data}
        pathSegments={[]}
        depth={0}
        expandedPaths={expandedPaths}
        searchQuery={searchQuery}
        searchPaths={searchPaths}
        onTogglePath={onTogglePath}
      />
    </div>
  );
};

export default JsonTreeViewer;
